import clsx from "clsx";
import type { ComponentProps, ReactNode } from "react";

import { CardListPagination } from "@widgets/card-list-pagination";
import { Section } from "@widgets/section";

import type { FilterItemType } from "@shared/content-types/types";

import type { PaginatedResponse } from "../../hooks/use-filter-pagination";
import { useFilterPagination } from "../../hooks/use-filter-pagination";
import { Container } from "../container";
import { Filters } from "../filters";

import s from "./filter-pagination-container.module.scss";

export interface FilterPaginationContainerProps<T> {
  initialContent: PaginatedResponse<T>;
  filters: FilterItemType[];
  fetchFunction: (
    filter: string,
    page: number,
  ) => Promise<PaginatedResponse<T>>;
  renderCard: (item: T, index: number) => ReactNode;
  className?: string;
  sectionClassName?: string;
  filtersClassName?: string;
  paginationClassName?: string;
  cardsClassName?: string;
  showFilters?: boolean;
  paginationThreshold?: number;
  filterProps?: Partial<ComponentProps<typeof Filters>>;
}

export const FilterPaginationContainer = <T,>({
  initialContent,
  filters,
  fetchFunction,
  renderCard,
  className,
  sectionClassName,
  filtersClassName,
  paginationClassName,
  cardsClassName,
  showFilters = true,
  paginationThreshold = 6,
  filterProps,
}: FilterPaginationContainerProps<T>) => {
  const {
    currentContent,
    handleChangeFilter,
    handleClickOnNext,
    handleClickOnPrev,
    handleClickOnPagination,
  } = useFilterPagination({
    initialContent,
    fetchFunction,
  });

  return (
    <Section className={clsx(s.root, sectionClassName)}>
      <Container className={className}>
        {showFilters && (filters?.length ?? 0) > 0 && (
          <Filters
            filters={filters ?? []}
            className={clsx(s.filters, filtersClassName)}
            onChangeFilter={handleChangeFilter}
            {...filterProps}
          />
        )}
        {currentContent?.data?.length > 0 && (
          <>
            <div className={cardsClassName}>
              {currentContent.data.map((item, index) =>
                item != null ? renderCard(item, index) : null,
              )}
            </div>
            {(currentContent?.meta?.pagination?.total ?? 0) >
              paginationThreshold && (
              <CardListPagination
                className={clsx(s.pagination, paginationClassName)}
                totalPages={currentContent?.meta?.pagination?.pageCount ?? 1}
                currentPageIndex={currentContent?.meta?.pagination?.page ?? 1}
                onNextClick={handleClickOnNext}
                onPrevClick={handleClickOnPrev}
                onPaginationClick={handleClickOnPagination}
              />
            )}
          </>
        )}
      </Container>
    </Section>
  );
};
